import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as yup from 'yup';
import {
  Box,
  TextInput,
  Button,
  Avatar,
  ActionIcon,
  Badge,
  Switch,
  Alert,
  Tooltip,
  Textarea,
  Title,
  Container,
  Group,
  Popover,
  Text,
  Flex,
  NumberInput,
  Space,
  Stack,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCamera,
  IconEdit,
  IconInfoCircle,
  IconShieldCheck,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { getCategories } from '../services/api';
import {
  getProfile,
  editProfile,
  getEmail,
  checkUsernameExists,
  SocialsPrefixes,
  ProfileValidation,
  normalizeSocialHandle,
} from '../services/ProfileService';
import { CategoryResponseModel } from '../types/Profile';
import { LANGUAGES } from '../constants/options';
import { getProfilePicture } from '../types/UserDocument';
import { NavigationWrapper } from '../components/navigation/NavigationWrapper';
import { PageRoutes } from '../constants/routes';
import { primaryColor } from '../constants/theme';
import { useApp } from '../context/AppContext';
import { CategoriesModal } from './edit-profile/CategoriesModal';
import { EditProfileSkeleton } from './edit-profile/EditProfileSkeleton';
import { LanguagesModal } from './edit-profile/LanguagesModal';
import { SearchTermsModal } from './edit-profile/SearchTermsModal';
import { IdNamePair } from './edit-profile/types';
import { ImageCropModal } from './edit-profile/ImageCropModal';

export type EditProfilePayload = {
  profilePicture?: string;
  websiteUrl: string;
  firstName: string;
  lastName: string;
  fullName: string;
  displayName: string;
  email: string;
  headline: string;
  headerBio: string;
  city: string;
  username: string;
  youTubeUrl: string;
  instagramUrl: string;
  facebookUrl: string;
  linkedInUrl: string;
  xUrl: string;
  tikTokUrl: string;
  about: string;
  categories: IdNamePair[];
  searchTerms: string;
  userLanguages: IdNamePair[];
  isProfessional: boolean;
};

const validationSchema = yup.object().shape({
  firstName: yup
    .string()
    .required('First Name is required')
    .matches(/^[a-zA-Z-\s]+$/, {
      message: 'First Name can only contain letters, hyphens, and spaces',
      excludeEmptyString: true,
    })
    .max(20, 'First Name cannot exceed 20 characters'),
  lastName: yup
    .string()
    .required('Last Name is required')
    .matches(/^[a-zA-Z-\s]+$/, {
      message: 'Last Name can only contain letters, hyphens, and spaces',
      excludeEmptyString: true,
    })
    .max(20, 'Last Name cannot exceed 20 characters'),
  displayName: yup.string().when('isProfessional', {
    is: true,
    then: (schema) =>
      schema
        .required('Display Name is required')
        .matches(
          /^[a-zA-Z0-9\s_-]+$/,
          'Display Name can only contain alphanumeric characters, spaces, underscores, and hyphens'
        )
        .max(100, 'Display Name cannot exceed 100 characters'),
    otherwise: (schema) => schema.notRequired(),
  }),
  email: yup
    .string()
    .required('Email is required')
    .email('Invalid email format')
    .max(256, 'Email cannot exceed 256 characters'),
  headline: yup.string().max(50, 'Headline cannot exceed 50 characters'),
  headerBio: yup.string().max(300, 'Header Bio cannot exceed 300 characters'),
  city: yup
    .string()
    .matches(/^[a-zA-Z-\s]+$/, {
      message: 'City can only contain letters, hyphens, and spaces',
      excludeEmptyString: true,
    })
    .max(50, 'City cannot exceed 50 characters'),
  username: yup.string().when('isProfessional', {
    is: true,
    then: (schema) =>
      schema
        .required('Username is required')
        .max(100, 'Username cannot exceed 100 characters'),
    otherwise: (schema) => schema.notRequired(),
  }),
  youTubeUrl: ProfileValidation.YOUTUBE,
  instagramUrl: ProfileValidation.INSTAGRAM,
  facebookUrl: ProfileValidation.FACEBOOK,
  linkedInUrl: ProfileValidation.LINKEDIN,
  tikTokUrl: ProfileValidation.TIKTOK,
  xUrl: ProfileValidation.X,
  websiteUrl: yup
    .string()
    .url('Please enter a valid website URL')
    .max(200, 'The website URL can only be a maximum of 200 characters'),
  about: yup.string().max(600, 'About cannot exceed 600 characters'),
});

const isRemoteImageUrl = (value: string) =>
  value.startsWith('http://') || value.startsWith('https://');

const blobToDataUrl = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }

      reject(new Error('Failed to encode image.'));
    });

    reader.addEventListener('error', () => {
      reject(new Error('Failed to encode image.'));
    });

    reader.readAsDataURL(blob);
  });
};

const ensureBase64ProfilePicture = async (profilePicture: string) => {
  if (!profilePicture || !isRemoteImageUrl(profilePicture)) {
    return profilePicture;
  }

  const response = await fetch(profilePicture);
  if (!response.ok) {
    throw new Error('Failed to load existing profile picture.');
  }

  const imageBlob = await response.blob();
  return blobToDataUrl(imageBlob);
};

export const EditProfile: React.FC = () => {
  const navigate = useNavigate();
  const [initallyProfessional, setInitiallyProfessional] = useState(false); // Used to determine whether saved professional status change
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [socialsError, setSocialError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [formData, setFormData] = useState({
    profilePicture: '',
    websiteUrl: '',
    firstName: '',
    lastName: '',
    fullName: '',
    displayName: '',
    email: '',
    headline: '',
    headerBio: '',
    city: '',
    username: '',
    youTubeUrl: '',
    instagramUrl: '',
    facebookUrl: '',
    linkedInUrl: '',
    tikTokUrl: '',
    xUrl: '',
    about: '',
    categories: [] as IdNamePair[],
    searchTerms: '',
    userLanguages: [] as IdNamePair[],
    isProfessional: false,
  });
  const [categories, setCategories] = useState<CategoryResponseModel[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<IdNamePair[]>(
    []
  );
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTerms, setSearchTerms] = useState<string[]>([]);
  const [isSearchTermsModalOpen, setIsSearchTermsModalOpen] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState<
    { id: number; name: string }[]
  >([]);
  const [isLanguagesModalOpen, setIsLanguagesModalOpen] = useState(false);

  const [isImageActionsModalOpen, setIsImageActionsModalOpen] = useState(false);
  const [isImageCropModalOpen, setIsImageCropModalOpen] = useState(false);
  const [tempImageSrc, setTempImageSrc] = useState<string | null>(null);
  const profilePictureInputRef = useRef<HTMLInputElement | null>(null);

  const initialUsernameRef = useRef('');
  const { accessToken } = useAuth();
  const RESOLUTION = 300;
  const { onSetProfile } = useApp();
  const queryClient = useQueryClient();
  const socialPrefixSectionProps = {
    style: {
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      paddingLeft: 'var(--mantine-spacing-xs)',
      paddingRight: 'var(--mantine-spacing-xs)',
    },
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      setIsLoading(true);
      try {
        if (accessToken) {
          const profileData = await getProfile(accessToken);
          setInitiallyProfessional(profileData.isProfessional);
          const email = await getEmail(accessToken);
          setFormData({
            profilePicture: profileData.profilePicture || '',
            websiteUrl: profileData.websiteUrl || '',
            firstName: profileData.firstName || '',
            lastName: profileData.lastName || '',
            fullName: profileData.fullName || '',
            displayName: profileData.displayName || '',
            email: email || '',
            headline: profileData.headline || '',
            headerBio: profileData.headerBio || '',
            city: profileData.city || '',
            username: profileData.username || '',
            youTubeUrl: profileData.youTubeProfileUrl
              ? normalizeSocialHandle(profileData.youTubeProfileUrl, 'youtube')
              : '',
            instagramUrl: profileData.instagramProfileUrl
              ? normalizeSocialHandle(profileData.instagramProfileUrl, 'instagram')
              : '',
            facebookUrl: profileData.facebookProfileUrl
              ? normalizeSocialHandle(profileData.facebookProfileUrl, 'facebook')
              : '',
            linkedInUrl: profileData.linkedInProfileUrl
              ? normalizeSocialHandle(profileData.linkedInProfileUrl, 'linkedin')
              : '',
            xUrl: profileData.xProfileUrl
              ? normalizeSocialHandle(profileData.xProfileUrl, 'x')
              : '',
            tikTokUrl: profileData.tikTokProfileUrl
              ? normalizeSocialHandle(profileData.tikTokProfileUrl, 'tiktok')
              : '',
            about: profileData.about || '',
            categories: profileData.userCategories ?? [],
            searchTerms:
              profileData.userSearchTerms
                ?.map((term) => term.name)
                .join(', ') || '',
            userLanguages: profileData.userLanguages || [],
            isProfessional: profileData.isProfessional || false,
          });
          setSelectedCategories(profileData.userCategories || []);
          setSearchTerms(
            profileData.userSearchTerms?.map((term) => term.name) || []
          );
          setSelectedLanguages(
            profileData.userLanguages?.map((language) => ({
              id: language.id,
              name: language.name,
            })) || []
          );
          initialUsernameRef.current = profileData.username || '';
        }
        setError(null);
      } catch (err) {
        setError('Failed to load profile');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getCategories(accessToken ?? '');
        setCategories(response.categories);
      } catch (error) {
        console.error('Failed to load categories', error);
      }
    };

    fetchCategories();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.currentTarget;
    const transformedValue =
      name === 'username' ? value.toLowerCase().replace(/ /g, '_') : value;
    setFormData({
      ...formData,
      [name]: transformedValue,
    });

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener('load', () => setTempImageSrc(reader.result?.toString() || ''));
      reader.readAsDataURL(file);
      setIsImageCropModalOpen(true);

      // Clear value so the same file triggers onChange again
      e.target.value = '';
    }
  };

  const handleSaveCroppedImage = (croppedBase64: string) => {
    setFormData({
      ...formData,
      profilePicture: croppedBase64,
    });
    setIsImageCropModalOpen(false);
    setTempImageSrc(null);
  };

  const handleOpenImageActions = () => {
    setIsImageActionsModalOpen(true);
  };

  const handleEditProfileImage = () => {
    setIsImageActionsModalOpen(false);
    profilePictureInputRef.current?.click();
  };

  const handleDeleteProfileImage = () => {
    setFormData((prev) => ({
      ...prev,
      profilePicture: '',
    }));
    setTempImageSrc(null);
    setIsImageCropModalOpen(false);
    setIsImageActionsModalOpen(false);
  };

  const handleCategoryToggle = (category: IdNamePair) => {
    setSelectedCategories((prev) =>
      prev.some((cat) => cat.id === category.id)
        ? prev.filter((cat) => cat.id !== category.id)
        : [...prev, category]
    );
  };

  const handleCategoriesModalOpen = () => {
    setIsCategoriesModalOpen(true);
  };

  const handleCategoriesModalSave = () => {
    setIsCategoriesModalOpen(false);
    setFormData({
      ...formData,
      categories: selectedCategories,
    });
  };

  const handleCategoriesModalCancel = () => {
    setIsCategoriesModalOpen(false);
  };

  const handleSearchTermsModalOpen = () => {
    setIsSearchTermsModalOpen(true);
  };

  const handleSearchTermsModalSave = () => {
    setIsSearchTermsModalOpen(false);
    setFormData({
      ...formData,
      searchTerms: searchTerms.join(', '),
    });
  };

  const handleSearchTermsModalCancel = () => {
    setIsSearchTermsModalOpen(false);
  };

  const handleAddTerm = () => {
    const trimmedTerm = searchTerm.trim();
    if (trimmedTerm) {
      if (
        !searchTerms.some((t) => t.toLowerCase() === trimmedTerm.toLowerCase())
      ) {
        setSearchTerms([...searchTerms, trimmedTerm]);
      }
      setSearchTerm('');
    }
  };

  const handleDeleteTerm = (term: string) => {
    setSearchTerms(searchTerms.filter((t) => t !== term));
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddTerm();
    }
  };

  const handleLanguagesModalOpen = () => {
    setIsLanguagesModalOpen(true);
  };

  const handleLanguagesModalSave = () => {
    setIsLanguagesModalOpen(false);
    setFormData({
      ...formData,
      userLanguages: selectedLanguages,
    });
  };

  const handleLanguagesModalCancel = () => {
    setIsLanguagesModalOpen(false);
  };

  const hasRequiredSocials = () => {
    const {
      facebookUrl,
      xUrl,
      linkedInUrl,
      instagramUrl,
      isProfessional,
      youTubeUrl,
      tikTokUrl,
    } = formData;

    if (!isProfessional) return true;

    return [
      facebookUrl,
      xUrl,
      linkedInUrl,
      instagramUrl,
      youTubeUrl,
      tikTokUrl,
    ].some((url) => url.trim() !== '');
  };

  useEffect(() => {
    if (hasRequiredSocials()) {
      setSocialError(null);
    }
  }, [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const normalizedFacebookUrl = normalizeSocialHandle(
        formData.facebookUrl,
        'facebook'
      );

      await validationSchema.validate(
        {
          ...formData,
          websiteUrl:
            formData.websiteUrl.trim() === '' ? undefined : formData.websiteUrl,
          youTubeUrl:
            formData.youTubeUrl.trim() === '' ? undefined : formData.youTubeUrl,
          instagramUrl:
            formData.instagramUrl.trim() === ''
              ? undefined
              : formData.instagramUrl,
          facebookUrl:
            formData.facebookUrl.trim() === ''
              ? undefined
              : normalizedFacebookUrl,
          linkedInUrl:
            formData.linkedInUrl.trim() === ''
              ? undefined
              : formData.linkedInUrl,
          tikTokUrl:
            formData.tikTokUrl.trim() === '' ? undefined : formData.tikTokUrl,
          xUrl: formData.xUrl.trim() === '' ? undefined : formData.xUrl,
        },
        { abortEarly: false }
      );
      if (!hasRequiredSocials()) {
        setSocialError('At least one social media profile is required');
        return;
      }
      if (
        formData.isProfessional &&
        formData.username !== initialUsernameRef.current &&
        formData.username.length > 0
      ) {
        try {
          const exists = await queryClient.fetchQuery({
            queryKey: ['username-exists', formData.username],
            queryFn: () => checkUsernameExists(formData.username),
            staleTime: Infinity,
          });
          if (exists) {
            setValidationErrors((prev) => ({
              ...prev,
              username: 'This username is already taken',
            }));
            return;
          }
        } catch {
          setValidationErrors((prev) => ({
            ...prev,
            username:
              'Could not verify username availability. Please try again.',
          }));
          return;
        }
      }
      if (!accessToken) {
        setError('User is not authenticated.');
        return;
      }
      setIsLoading(true);
      const encodedProfilePicture = await ensureBase64ProfilePicture(
        formData.profilePicture
      );
      const { profilePicture: _profilePicture, ...restFormData } = formData;
      const payload: EditProfilePayload = {
        ...restFormData,
        facebookUrl: normalizedFacebookUrl,
        ...(encodedProfilePicture
          ? { profilePicture: encodedProfilePicture }
          : {}),
        headline: formData.isProfessional ? formData.headline : '',
      };
      const updatedProfile = await editProfile(payload, accessToken);
      if (updatedProfile) {
        onSetProfile(updatedProfile);

        navigate(`${PageRoutes.PROFILE}?professionalId=${updatedProfile?.id}`);
      }
    } catch (validationErrors) {
      if (validationErrors instanceof yup.ValidationError) {
        const errors = validationErrors.inner.reduce(
          (acc, err) => {
            if (err.path) acc[err.path] = err.message;
            return acc;
          },
          {} as Record<string, string>
        );
        setValidationErrors(errors);
      } else {
        setError('Failed to save profile. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const profileSrc = useMemo(() => {
    return getProfilePicture(formData.profilePicture);
  }, [formData.profilePicture]);

  const { isTabletAndBelow, scrollAreaHeight } = useApp();

  if (isLoading) {
    return <EditProfileSkeleton />;
  }

  return (
    <NavigationWrapper>
      <Container
        size={isTabletAndBelow ? '100%' : 1400}
        py={{ base: 0, sm: 'lg', md: 'xl' }}
        px={{ base: 0, sm: 'lg' }}
        style={{ width: '100%' }}
      >
        <Box
          style={{
            backgroundColor: '#fff',
            padding: isTabletAndBelow
              ? 'var(--mantine-spacing-md)'
              : 'var(--mantine-spacing-lg)',
            boxShadow: '0 1px 8px rgba(0, 0, 0, 0.1)',
            minHeight: isTabletAndBelow ? scrollAreaHeight : 'auto',
            width: '100%',
            maxWidth: isTabletAndBelow ? '100%' : 1200,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <Stack maw={isTabletAndBelow ? '100%' : 800} mx="auto">
            <Title order={3} fw="normal" mb="lg" data-testid="edit-profile-title">
              Edit Profile
            </Title>
            {error && (
              <Alert color="red" mb="md" data-testid="error-alert">
                {error}
              </Alert>
            )}
            <form onSubmit={handleSubmit} noValidate>
              <Flex justify="center" mb="lg">
                <Box style={{ position: 'relative', display: 'inline-block' }}>
                  <Avatar
                    src={profileSrc}
                    size={150}
                    radius={150}
                    data-testid="profile-avatar"
                  />
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="profile-picture-upload"
                    type="file"
                    ref={profilePictureInputRef}
                    onChange={handleImageFileChange}
                    data-testid="profile-picture-input"
                  />
                  <Popover
                    opened={isImageActionsModalOpen}
                    onChange={setIsImageActionsModalOpen}
                    position="bottom-end"
                    withArrow
                    hideDetached={false}
                    shadow="md"
                  >
                    <Popover.Target>
                      <ActionIcon
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          borderRadius: '50%',
                          width: 40,
                          height: 40,
                        }}
                        color="gray"
                        variant="filled"
                        onClick={handleOpenImageActions}
                        data-testid="change-profile-picture-button"
                      >
                        <IconCamera size={20} />
                      </ActionIcon>
                    </Popover.Target>
                    <Popover.Dropdown p="lg">
                      <Stack gap="xs">
                        <Button
                          color={primaryColor}
                          onClick={handleEditProfileImage}
                          data-testid="edit-profile-image-button"
                        >
                          Edit image
                        </Button>
                        <Button
                        color={primaryColor}
                                                  variant='outline'

                          onClick={handleDeleteProfileImage}
                          disabled={!formData.profilePicture}
                          data-testid="delete-profile-image-button"
                        >
                          Delete image
                        </Button>
                      </Stack>
                    </Popover.Dropdown>
                  </Popover>
                </Box>
              </Flex>
              {formData.isProfessional && (
                <TextInput
                  label="Display Name"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleInputChange}
                  required
                  maxLength={100}
                  error={validationErrors.displayName}
                  mb="sm"
                  size="md"
                  fw="lighter"
                  description="This is your public-facing name"
                  styles={{ description: { textAlign: 'left' } }}
                  data-testid="display-name-input"
                />
              )}
              {formData.isProfessional && (
                <TextInput
                  label="I am a"
                  name="headline"
                  value={formData.headline}
                  onChange={handleInputChange}
                  maxLength={50}
                  error={validationErrors.headline}
                  mb="sm"
                  size="md"
                  fw="lighter"
                  data-testid="headline-input"
                />
              )}
              <Textarea
                label="Header Bio"
                name="headerBio"
                value={formData.headerBio}
                onChange={handleInputChange}
                minRows={6}
                maxLength={300}
                error={validationErrors.headerBio}
                mb="sm"
                size="md"
                fw="lighter"
                data-testid="header-bio-input"
              />
              <TextInput
                label="City"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                maxLength={50}
                error={validationErrors.city}
                mb="sm"
                size="md"
                fw="lighter"
                data-testid="city-input"
              />
              {formData.isProfessional && (
                <TextInput
                  label="Shared Profile Handle"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  maxLength={100}
                  error={validationErrors.username}
                  mb="sm"
                  size="md"
                  fw="lighter"
                  data-testid="username-input"
                />
              )}
              <TextInput
                label="Website Url"
                name="websiteUrl"
                value={formData.websiteUrl}
                onChange={handleInputChange}
                maxLength={200}
                error={validationErrors.websiteUrl}
                mb="sm"
                size="md"
                fw="lighter"
                data-testid="website-url-input"
              />
              <TextInput
                label="YouTube URL"
                name="youTubeUrl"
                value={formData.youTubeUrl}
                onChange={handleInputChange}
                leftSection={
                  <Text size="sm" c="dimmed" lh={1}>
                    {SocialsPrefixes.YOUTUBE}
                  </Text>
                }
                leftSectionWidth={170}
                leftSectionProps={socialPrefixSectionProps}
                maxLength={100}
                error={validationErrors.youTubeUrl}
                mb="sm"
                size="md"
                fw="lighter"
                data-testid="youtube-url-input"
              />
              <TextInput
                label="Instagram URL"
                name="instagramUrl"
                value={formData.instagramUrl}
                onChange={handleInputChange}
                leftSection={
                  <Text size="sm" c="dimmed" lh={1}>
                    {SocialsPrefixes.INSTAGRAM}
                  </Text>
                }
                leftSectionWidth={150}
                leftSectionProps={socialPrefixSectionProps}
                maxLength={100}
                error={validationErrors.instagramUrl}
                mb="sm"
                size="md"
                fw="lighter"
                data-testid="instagram-url-input"
              />
              <TextInput
                label="Facebook URL"
                name="facebookUrl"
                value={formData.facebookUrl}
                onChange={handleInputChange}
                leftSection={
                  <Text size="sm" c="dimmed" lh={1}>
                    {SocialsPrefixes.FACEBOOK}
                  </Text>
                }
                leftSectionWidth={140}
                leftSectionProps={socialPrefixSectionProps}
                maxLength={100}
                error={validationErrors.facebookUrl}
                mb="sm"
                size="md"
                fw="lighter"
                data-testid="facebook-url-input"
              />
              <TextInput
                label="LinkedIn URL"
                name="linkedInUrl"
                value={formData.linkedInUrl}
                onChange={handleInputChange}
                leftSection={
                  <Text size="sm" c="dimmed" lh={1}>
                    {SocialsPrefixes.LINKEDIN}
                  </Text>
                }
                leftSectionWidth={140}
                leftSectionProps={socialPrefixSectionProps}
                maxLength={100}
                error={validationErrors.linkedInUrl}
                mb="sm"
                size="md"
                fw="lighter"
                data-testid="linkedin-url-input"
              />
              <TextInput
                label="X URL"
                name="xUrl"
                value={formData.xUrl}
                onChange={handleInputChange}
                leftSection={
                  <Text size="sm" c="dimmed" lh={1}>
                    {SocialsPrefixes.X}
                  </Text>
                }
                leftSectionWidth={100}
                leftSectionProps={socialPrefixSectionProps}
                maxLength={100}
                error={validationErrors.xUrl}
                mb="sm"
                size="md"
                fw="lighter"
                data-testid="x-url-input"
              />
              <TextInput
                label="Tiktok URL"
                name="tikTokUrl"
                value={formData.tikTokUrl}
                onChange={handleInputChange}
                leftSection={
                  <Text size="sm" c="dimmed" lh={1}>
                    {SocialsPrefixes.TIKTOK}
                  </Text>
                }
                leftSectionWidth={170}
                leftSectionProps={socialPrefixSectionProps}
                maxLength={100}
                error={validationErrors.tikTokUrl}
                mb="sm"
                size="md"
                fw="lighter"
                data-testid="tiktok-url-input"
              />
              {!formData.isProfessional && (
                <Textarea
                  label="About"
                  name="about"
                  value={formData.about}
                  onChange={handleInputChange}
                  minRows={6}
                  maxLength={600}
                  error={validationErrors.about}
                  mb="sm"
                  size="md"
                  fw="lighter"
                  data-testid="about-input"
                />
              )}
              {formData.isProfessional && (
                <Box mb="sm">
                  <Text size="lg" fw={500} mb="xs" ta="left">
                    Categories
                  </Text>
                  <Box
                    style={{
                      border: '1px solid var(--mantine-color-gray-4)',
                      borderRadius: 'var(--mantine-radius-sm)',
                      padding: 'var(--mantine-spacing-md)',
                      position: 'relative',
                    }}
                  >
                    <ActionIcon
                      style={{ position: 'absolute', top: 5, right: 5 }}
                      onClick={handleCategoriesModalOpen}
                      color={primaryColor}
                      variant="subtle"
                      data-testid="edit-categories-button"
                    >
                      <IconEdit size={20} />
                    </ActionIcon>
                    <Group gap="xs">
                      {formData.categories.map((category) => (
                        <Badge
                          key={category.id}
                          radius="sm"
                          data-testid={`category-badge-${category.id}`}
                          color={primaryColor}
                        >
                          {category.name}
                        </Badge>
                      ))}
                      {formData.categories.length === 0 && (
                        <Text size="sm" c="dimmed">
                          No categories selected
                        </Text>
                      )}
                    </Group>
                  </Box>
                </Box>
              )}
              {formData.isProfessional && (
                <Box mb="sm">
                  <Text size="lg" fw={500} mb="xs" ta="left">
                    Search Terms
                  </Text>
                  <Box
                    style={{
                      border: '1px solid var(--mantine-color-gray-4)',
                      borderRadius: 'var(--mantine-radius-sm)',
                      padding: 'var(--mantine-spacing-md)',
                      position: 'relative',
                    }}
                  >
                    <ActionIcon
                      style={{ position: 'absolute', top: 5, right: 5 }}
                      onClick={handleSearchTermsModalOpen}
                      color={primaryColor}
                      variant="subtle"
                      data-testid="edit-search-terms-button"
                    >
                      <IconEdit size={20} />
                    </ActionIcon>
                    <Group gap="xs">
                      {formData.searchTerms
                        .split(', ')
                        .filter(Boolean)
                        .map((term, index) => (
                          <Badge
                            key={index}
                            radius="sm"
                            data-testid={`search-term-badge-${index}`}
                            color={primaryColor}
                          >
                            {term}
                          </Badge>
                        ))}
                      {formData.searchTerms === '' && (
                        <Text size="sm" c="dimmed">
                          No search terms added
                        </Text>
                      )}
                    </Group>
                  </Box>
                </Box>
              )}
              <Box mb="sm">
                <Text size="lg" fw={500} mb="xs" ta="left">
                  Preferred Languages
                </Text>
                <Box
                  style={{
                    border: '1px solid var(--mantine-color-gray-4)',
                    borderRadius: 'var(--mantine-radius-sm)',
                    padding: 'var(--mantine-spacing-md)',
                    position: 'relative',
                  }}
                >
                  <ActionIcon
                    style={{ position: 'absolute', top: 5, right: 5 }}
                    onClick={handleLanguagesModalOpen}
                    color={primaryColor}
                    variant="subtle"
                    data-testid="edit-languages-button"
                  >
                    <IconEdit size={20} />
                  </ActionIcon>
                  <Group gap="xs">
                    {formData.userLanguages.map((language) => (
                      <Badge
                        key={language.id}
                        radius="sm"
                        data-testid={`language-badge-${language.id}`}
                        color={primaryColor}
                      >
                        {language.name}
                      </Badge>
                    ))}
                    {formData.userLanguages.length === 0 && (
                      <Text size="sm" c="dimmed">
                        No languages selected
                      </Text>
                    )}
                  </Group>
                </Box>
              </Box>
              <Text size="sm" c="dimmed" mb="sm">
                Once you become a Content Creator or Influencer and save the
                changes, you will not be able to reverse this change.
              </Text>
              <Group mb="sm" align="center">
                <Switch
                  name="isProfessional"
                  checked={formData.isProfessional}
                  disabled={initallyProfessional}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      isProfessional: e.currentTarget.checked,
                    })
                  }
                  label="Is Creator / Influencer?"
                  size="md"
                  data-testid="professional-toggle"
                />
                <Tooltip label="This is if you are a Content Creator or Influencer and wish to host video calls with your followers and subscribers.">
                  <ActionIcon variant="subtle" size="sm" color={primaryColor}>
                    <IconInfoCircle size={18} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              {socialsError && (
                <Alert
                  color="red"
                  mb="md"
                  onClose={() => setSocialError(null)}
                  withCloseButton
                  data-testid="socials-error-alert"
                >
                  {socialsError}
                </Alert>
              )}
              <Space h="md" />
              {formData.isProfessional && (
                <Group mt="xl" mb="md" gap="xs">
                  <IconShieldCheck size={24} color={primaryColor} />
                  <Title order={4} data-testid="private-info-header">
                    Private Information
                  </Title>
                </Group>
              )}
              <TextInput
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                maxLength={20}
                error={validationErrors.firstName}
                mb="sm"
                size="md"
                fw="lighter"
                data-testid="first-name-input"
              />
              <TextInput
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                maxLength={20}
                error={validationErrors.lastName}
                mb="sm"
                size="md"
                fw="lighter"
                data-testid="last-name-input"
              />
              <TextInput
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                maxLength={256}
                error={validationErrors.email}
                mb="sm"
                size="md"
                fw="lighter"
                data-testid="email-input"
              />
              {Object.keys(validationErrors).length > 0 && (
                <Alert
                  color="red"
                  mb="md"
                  data-testid="validation-error-alert"
                  icon={<IconAlertCircle size={20} />}
                  title="Validation Errors"
                >
                  Please fix validation errors, scroll up to see affected fields.
                </Alert>
              )}
              <Button
                type="submit"
                color={primaryColor}
                fullWidth
                radius="md"
                size="md"
                data-testid="update-profile-button"
              >
                Update Profile
              </Button>
              <Space h={20} />
              {tempImageSrc && (
                <ImageCropModal
                  opened={isImageCropModalOpen}
                  onClose={() => {
                    setIsImageCropModalOpen(false);
                    setTempImageSrc(null);
                  }}
                  imageSrc={tempImageSrc}
                  onSave={handleSaveCroppedImage}
                />
              )}
            </form>
          </Stack>
        </Box>
      </Container>
      <CategoriesModal
        opened={isCategoriesModalOpen}
        categories={categories}
        selectedCategories={selectedCategories}
        onToggleCategory={handleCategoryToggle}
        onSave={handleCategoriesModalSave}
        onCancel={handleCategoriesModalCancel}
      />
      <SearchTermsModal
        opened={isSearchTermsModalOpen}
        searchTerm={searchTerm}
        searchTerms={searchTerms}
        onSearchTermChange={setSearchTerm}
        onAddTerm={handleAddTerm}
        onDeleteTerm={handleDeleteTerm}
        onKeyPress={handleKeyPress}
        onSave={handleSearchTermsModalSave}
        onCancel={handleSearchTermsModalCancel}
      />
      <LanguagesModal
        opened={isLanguagesModalOpen}
        options={LANGUAGES.map((lang) => ({
          value: lang.id.toString(),
          label: lang.name,
        }))}
        value={selectedLanguages.map((language) => language.id.toString())}
        onChange={(values) => {
          const newLanguages = values
            .map((val) => {
              const lang = LANGUAGES.find((l) => l.id.toString() === val);
              return lang ? { id: lang.id, name: lang.name } : null;
            })
            .filter(
              (lang): lang is { id: number; name: string } => lang !== null
            );
          setSelectedLanguages(newLanguages);
        }}
        onSave={handleLanguagesModalSave}
        onCancel={handleLanguagesModalCancel}
      />
    </NavigationWrapper>
  );
};

export default EditProfile;
